import { useState, useEffect, useRef, useCallback } from "react";
import {
    ref,
    onValue,
    off,
    query,
    limitToLast,
    endBefore,
    get,
    orderByChild,
    set,
    update
} from "firebase/database";
import { database } from "../constants/firebase";

export const useChatMessages = (chatType, chatId, currentUser) => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const previousMessageCount = useRef(0);
    const unsubscribeRef = useRef(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const isLoadingMoreRef = useRef(false);
    const lastKeyRef = useRef(null);
    const [noMoreMessages, setNoMoreMessages] = useState(false);

    const PAGE_SIZE = 20;

    const getMessagesPath = useCallback(() => {
        switch (chatType) {
            case "private":
                return `privateChats/${chatId}`;
            case "group":
                return `groupChats/${chatId}`;
            case "community":
                return `communityMessages/${chatId}`;
            default:
                return null;
        }
    }, [chatType, chatId]);

    useEffect(() => {
        if (!chatId || !currentUser) return;
        if (!messages.length) return;

        // 🚨 BLOCK pagination completely
        if (isLoadingMoreRef.current) return;

        const messagesPath = getMessagesPath();
        if (!messagesPath) return;

        const lastMessage = messages[messages.length - 1];
        if (!lastMessage) return;

        // ignore own messages
        if (lastMessage.senderId === currentUser.uid) return;

        let updates = {};

        if (chatType === "private") {
            if (!lastMessage.seen) {
                updates[`${messagesPath}/${lastMessage.id}/seen`] = true;
            }
        }

        if (chatType === "group") {
            if (!lastMessage.seenBy?.[currentUser.uid]) {
                updates[`${messagesPath}/${lastMessage.id}/seenBy/${currentUser.uid}`] = true;
            }
        }

        if (chatType === "community") {
            if (!lastMessage.isSeen?.[currentUser.uid]) {
                updates[`${messagesPath}/${lastMessage.id}/isSeen/${currentUser.uid}`] = true;
            }
        }

        if (Object.keys(updates).length > 0) {
            update(ref(database), updates);
        }

    }, [messages, chatType, chatId, currentUser, getMessagesPath]);

    // ==============================
    // REALTIME LISTENER (ONLY LAST 20)
    // ==============================
    useEffect(() => {
        if (!chatId || !currentUser) return;

        const path = getMessagesPath();
        if (!path) return;

        // cleanup old listener
        if (unsubscribeRef.current) {
            unsubscribeRef.current();
        }

        let q;
        if (chatType === 'community') {
            // For community, we want to listen to all messages but only load the last 20           
            q = query(
                ref(database, path),
                orderByChild("timestamp"),
                limitToLast(PAGE_SIZE)
            );
        } else {
            // For private and group chats, we can listen to the whole path since we filter deleted messages client-side
            q = ref(database, path);
        }

        const unsubscribe = onValue(q, (snapshot) => {
            if (!snapshot.exists()) {
                setMessages([]);
                setHasMore(false);
                setNoMoreMessages(true);
                setLoading(false);
                return;
            }

            const data = snapshot.val();

            let msgs = Object.entries(data).map(([id, val]) => ({
                id,
                ...val,
            }));

            // Apply filters based on chat type
            if (chatType === 'group') {
                msgs = msgs.filter(msg => {
                    if (msg.deletedFor?.[currentUser?.uid]) return false;
                    if (msg.deleted && msg.movedToTrash) return false;
                    return true;
                });
            }


            msgs.sort((a, b) => a.timestamp - b.timestamp);

            setMessages(msgs);

            // store oldest message for pagination
            if (msgs.length > 0) {
                lastKeyRef.current = msgs[0];
            }

            const hasMoreInitial = msgs.length === PAGE_SIZE;

            setHasMore(hasMoreInitial);
            setNoMoreMessages(!hasMoreInitial);
            setLoading(false);
        });

        unsubscribeRef.current = unsubscribe;

        return () => {
            if (unsubscribeRef.current) unsubscribeRef.current();
        };
    }, [chatId, chatType, currentUser, getMessagesPath]);

    // ==============================
    // LOAD MORE (NO LISTENER USED)
    // ==============================
    const loadMoreMessages = async () => {
        if (loadingMore || !hasMore || !lastKeyRef.current) return;

        setLoadingMore(true);
        isLoadingMoreRef.current = true;

        try {
            const path = getMessagesPath();
            if (!path) return;

            const oldest = lastKeyRef.current;

            const q = query(
                ref(database, path),
                orderByChild("timestamp"),
                endBefore(oldest.timestamp),
                limitToLast(15)
            );

            const snap = await get(q);

            if (!snap.exists()) {
                setHasMore(false);
                setNoMoreMessages(true);
                return;
            }

            const olderMsgs = Object.entries(snap.val()).map(([id, val]) => ({
                id,
                ...val,
            }));

            olderMsgs.sort((a, b) => a.timestamp - b.timestamp);

            setMessages(prev => {
                const map = new Map();

                [...olderMsgs, ...prev].forEach(m => map.set(m.id, m));

                return Array.from(map.values()).sort(
                    (a, b) => a.timestamp - b.timestamp
                );
            });

            // 🔥 IMPORTANT FIX
            lastKeyRef.current = olderMsgs[0] || lastKeyRef.current;

            const hasMoreData = olderMsgs.length === 15;

            setHasMore(hasMoreData);
            setNoMoreMessages(!hasMoreData);


        } catch (err) {
            console.error("loadMoreMessages error:", err);
        } finally {
            setLoadingMore(false);

            setTimeout(() => {
                isLoadingMoreRef.current = false;
            }, 50);
        }
    };

    // ==============================
    // DELETE MESSAGE
    // ==============================
    const deleteMessage = useCallback(async (messageId) => {
        const path = getMessagesPath();
        if (!path) return;

        await set(ref(database, `${path}/${messageId}`), null);
    }, [getMessagesPath]);

    // ==============================
    // REACTIONS
    // ==============================
    const addReaction = useCallback(async (messageId, emoji) => {
        const path = getMessagesPath();
        if (!path) return;

        await set(
            ref(database, `${path}/${messageId}/reactions/${currentUser.uid}`),
            emoji
        );
    }, [getMessagesPath, currentUser]);

    const removeReaction = useCallback(async (messageId) => {
        const path = getMessagesPath();
        if (!path) return;

        await set(
            ref(database, `${path}/${messageId}/reactions/${currentUser.uid}`),
            null
        );
    }, [getMessagesPath, currentUser]);

    return {
        messages,
        loading,
        loadMoreMessages,
        loadingMore,
        noMoreMessages,
        hasMore,
        deleteMessage,
        addReaction,
        removeReaction,
        previousMessageCount: previousMessageCount.current
    };
};